import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { MultipleQueriesResponse, SearchResponse } from '@algolia/client-search';
import { Injectable } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';
import { SchemaListUpdate, Table, TablesUpdate } from '@mode/shared/contract-common';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { QueryOptions, RecordNames, RecordTypes } from '@mode/shared/data-access-algolia';
import { EnvironmentFacade } from '@mode/shared/environment/contract';
import { ACCEPT_BASIC_JSON, ADD_DATA_INDEX_AUTH } from '@mode/shared/util-halogen';
import { toSchemaListUpdate, toTable, toTablesUpdate } from './data-index-data-mappers';

type RequestDetails = {
  dataSourceToken: string;
  nodeId?: string;
  page?: number;
};

@Injectable({ providedIn: 'root' })
export class DataIndexClientService {
  private dataIndexBase = 'dataindex'; // Fallback to dataindex if env is not loaded

  constructor(
    private http: HttpClient,
    private environmentFacade: EnvironmentFacade
  ) {
    this.environmentFacade.dataIndexBase$.subscribe((dataIndexBase) => (this.dataIndexBase = dataIndexBase));
  }

  public async getSchemas(dataSourceToken: string, page?: number): Promise<SchemaListUpdate> {
    return await firstValueFrom(
      this._getNode({ dataSourceToken, page }).pipe(map((response: any) => toSchemaListUpdate(response)))
    );
  }

  public async getSchema(dataSourceToken: string, schemaId: string): Promise<TablesUpdate> {
    return await firstValueFrom(
      this._getNode({ dataSourceToken, nodeId: schemaId }).pipe(map((response: any) => toTablesUpdate(response)))
    );
  }

  public async getTable(dataSourceToken: string, schemaId: string, tableId: string): Promise<Table> {
    return await firstValueFrom(
      this._getNode({ dataSourceToken, nodeId: tableId }).pipe(map((response: any) => toTable(response, schemaId)))
    );
  }

  public async getTables(dataSourceToken: string, schemaId: string, page: number): Promise<TablesUpdate> {
    return await firstValueFrom(
      this._getNode({
        dataSourceToken,
        nodeId: schemaId,
        page,
      }).pipe(map((response: any) => toTablesUpdate(response)))
    );
  }

  public async search<Input extends RecordNames, Output extends RecordTypes[Input]>(
    dataSourceToken: string,
    opts: QueryOptions<Input>
  ): Promise<SearchResponse<Output>[]> {
    return firstValueFrom(
      this._post<MultipleQueriesResponse<Output>>(`${this.dataIndexBase}/api/datasource/${dataSourceToken}/search`, {
        datasourceToken: dataSourceToken,
        query: opts.query,
        indexDetails: opts.indexes.map((index) => {
          const sanitizedIndex = index === 'table' ? 'dataSourceTable' : index;
          return {
            index: sanitizedIndex,
            fields: opts.attrsToRetrieve ?? ['*'],
            searchFields: opts.attrsToSearchIn?.[index],
          };
        }),
      })
    ).then((r) => r.results as SearchResponse<Output>[]);
  }

  private _getNode(requestDetails: RequestDetails) {
    let url = `${this.dataIndexBase}/api/datasource/${requestDetails.dataSourceToken}/node`;

    if (requestDetails.nodeId) {
      url += `/${requestDetails.nodeId}`;
    }

    if (requestDetails.page !== undefined) {
      url += `?page=${requestDetails.page}`;
    }

    return this._get(url);
  }

  private _post<T = object>(url: string, body: any) {
    const context = new HttpContext();
    context.set(ACCEPT_BASIC_JSON, true);
    context.set(ADD_DATA_INDEX_AUTH, true);

    return this.http.post<T>(url, body, { context });
  }

  private _get<T = object>(url: string, params?: HttpParams) {
    const context = new HttpContext();
    context.set(ACCEPT_BASIC_JSON, true);
    context.set(ADD_DATA_INDEX_AUTH, true);

    return this.http.get<T>(url, { context, params });
  }
}
